import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Building,
  DollarSign,
  Calendar,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Home,
  Target,
  Search
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { API } from '../utils/config';


const KPIPartnerManager = () => {
  const [viewType, setViewType] = useState('partners'); // 'partners' or 'managers'
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchKPIData();
  }, [viewType, year]);

  const fetchKPIData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const endpoint = viewType === 'partners' 
        ? `/api/kpi/partners?year=${year}`
        : `/api/kpi/property-managers?year=${year}`;
      
      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setData(response.data);
    } catch (error) {
      console.error('Error fetching KPI data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const formatPercent = (value) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const getGrowthIcon = (value) => {
    if (value > 0) return <ArrowUpRight className="h-4 w-4 text-green-600" />;
    if (value < 0) return <ArrowDownRight className="h-4 w-4 text-red-600" />;
    return null;
  };

  const getGrowthColor = (value) => {
    if (value > 0) return 'text-green-600';
    if (value < 0) return 'text-red-600';
    return 'text-slate-600';
  };

  if (loading) {
    return (
      <Layout title={viewType === 'partners' ? 'KPI Partner' : 'KPI Responsabili'}>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
            <p className="text-slate-600">Caricamento KPI...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const allItems = viewType === 'partners' ? data?.partners : data?.managers;
  const itemsLabel = viewType === 'partners' ? 'Partner' : 'Responsabili';
  
  // Filter items based on search term
  const items = allItems?.filter(item => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    if (viewType === 'partners') {
      // Search in partner name, code, and city
      return item.partner_name?.toLowerCase().includes(searchLower) ||
             item.partner_code?.toLowerCase().includes(searchLower) ||
             item.entity_type?.toLowerCase().includes(searchLower);
    } else {
      // Search in manager name, operation area
      return item.manager_name?.toLowerCase().includes(searchLower) ||
             item.operation_area?.toLowerCase().includes(searchLower) ||
             item.entity_type?.toLowerCase().includes(searchLower);
    }
  });

  return (
    <Layout title={`KPI ${itemsLabel}`}>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="h-8 w-8 text-blue-900" />
              KPI {itemsLabel}
            </h1>
            <p className="text-slate-600 mt-2">
              Performance e metriche operative {year}
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 border-0 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Visualizza
              </label>
              <Select value={viewType} onValueChange={setViewType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="partners">📊 Partner</SelectItem>
                  <SelectItem value="managers">👨‍💼 Responsabili</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Anno
              </label>
              <Select value={year.toString()} onValueChange={(val) => setYear(parseInt(val))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2023">2023</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Cerca
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <Input
                  placeholder={`Cerca ${viewType === 'partners' ? 'partner' : 'responsabile'}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-6 border-0 shadow-lg card-hover">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-900" />
              </div>
            </div>
            <p className="text-sm text-slate-600">Totale {itemsLabel}</p>
            <p className="text-3xl font-bold text-slate-900">
              {viewType === 'partners' ? data?.partners_count : data?.managers_count}
            </p>
          </Card>

          <Card className="p-6 border-0 shadow-lg card-hover">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Home className="h-6 w-6 text-green-900" />
              </div>
            </div>
            <p className="text-sm text-slate-600">Immobili Totali</p>
            <p className="text-3xl font-bold text-slate-900">{data?.total_properties}</p>
          </Card>

          <Card className="p-6 border-0 shadow-lg card-hover">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-900" />
              </div>
            </div>
            <p className="text-sm text-slate-600">Ricavi Totali</p>
            <p className="text-3xl font-bold text-slate-900">
              {formatCurrency(data?.total_revenue || 0)}
            </p>
          </Card>

          <Card className="p-6 border-0 shadow-lg card-hover">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Calendar className="h-6 w-6 text-amber-900" />
              </div>
            </div>
            <p className="text-sm text-slate-600">Prenotazioni Totali</p>
            <p className="text-3xl font-bold text-slate-900">{data?.total_bookings}</p>
          </Card>
        </div>

        {/* Performance Table */}
        <Card className="overflow-hidden shadow-lg">
          <div className="p-6 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-900" />
              Dettaglio Performance
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    {viewType === 'partners' ? 'Partner' : 'Responsabile'}
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    Immobili
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">
                    Ricavi
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    Prenotazioni
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    ADR
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    Occupazione
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    Crescita YoY
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {items && items.length > 0 ? (
                  items.map((item, index) => (
                    <tr key={index} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900">
                            {viewType === 'partners' ? item.partner_name : item.manager_name}
                          </p>
                          <p className="text-sm text-slate-500">
                            {viewType === 'partners' ? item.partner_code : item.operation_area}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {item.properties_count}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <p className="font-semibold text-slate-900">
                          {formatCurrency(item.total_revenue)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatCurrency(item.avg_revenue_per_property)}/immobile
                        </p>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <p className="font-medium text-slate-900">{item.total_bookings}</p>
                        <p className="text-xs text-slate-500">{item.total_nights} notti</p>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <p className="font-medium text-slate-900">
                          {formatCurrency(item.adr)}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <div className="w-16 bg-slate-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all"
                              style={{ width: `${Math.min(item.occupancy_rate, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-slate-700">
                            {item.occupancy_rate.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {getGrowthIcon(item.revenue_growth_yoy)}
                          <span className={`font-medium ${getGrowthColor(item.revenue_growth_yoy)}`}>
                            {formatPercent(item.revenue_growth_yoy)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          vs {formatCurrency(item.prev_year_revenue)}
                        </p>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                      <BarChart3 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-lg font-medium">
                        {searchTerm ? 'Nessun risultato trovato' : 'Nessun dato disponibile'}
                      </p>
                      <p className="text-sm mt-2">
                        {searchTerm 
                          ? `Nessun ${itemsLabel.toLowerCase().slice(0, -1)} corrisponde a "${searchTerm}"`
                          : `Non ci sono ${itemsLabel.toLowerCase()} con dati per l'anno ${year}`
                        }
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Top Performers Chart */}
        {items && items.length > 0 && (
          <Card className="p-6 border-0 shadow-lg">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-900" />
              Top 10 per Ricavi
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={items.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey={viewType === 'partners' ? 'partner_name' : 'manager_name'} 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="total_revenue" fill="#1e3a8a" name="Ricavi Totali" />
                <Bar dataKey="prev_year_revenue" fill="#94a3b8" name="Anno Precedente" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default KPIPartnerManager;
